import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { useTranslation } from 'react-i18next';
import { Title } from '../components/ui/Title';
import { Loader2 } from 'lucide-react';

const createProfileSchema = z.object({
  full_name: z.string().min(2, 'Full name must be at least 2 characters').max(100, 'Full name cannot exceed 100 characters'),
  artist_name: z.string().max(50, 'Artist name cannot exceed 50 characters').optional(),
  gender: z.enum(['male', 'female', 'diverse'], {
    required_error: 'Please select your gender',
  }),
  is_musician: z.boolean().default(false),
});

type CreateProfileFormData = z.infer<typeof createProfileSchema>;

function CreateProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, loading: profileLoading, refreshProfile } = useProfile();
  const [error, setError] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [pendingInvitation, setPendingInvitation] = React.useState<string | null>(null);
  
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<CreateProfileFormData>({
    resolver: zodResolver(createProfileSchema),
    defaultValues: {
      is_musician: false, // Default to false
    }
  });

  const isMusician = watch('is_musician');

  // Check for pending invitation
  useEffect(() => {
    const storedInvitation = localStorage.getItem('pendingInvitation');
    if (storedInvitation) {
      setPendingInvitation(storedInvitation);
      // If there's an invitation, ensure is_musician is set to true
      setValue('is_musician', true);
    }
  }, [setValue]);

  // Redirect logic
  useEffect(() => {
    if (!profileLoading) {
      // If user is not logged in, redirect to login
      if (!user) {
        navigate('/login');
        return;
      }

      // If user already has a profile, redirect to profile page
      if (profile) {
        navigate('/profile');
        return;
      }
    }
  }, [user, profile, profileLoading, navigate]);

  const processInvitation = async (profileId: string) => {
    if (!pendingInvitation || !user) return;

    try {
      const { data: invitationData, error: invitationError } = await supabase
        .from('invitations')
        .select('*, band:bands!invitations_band_id_fkey(*)')
        .eq('invitation_token', pendingInvitation)
        .eq('status', 'pending')
        .single();

      if (invitationError || !invitationData) {
        localStorage.removeItem('pendingInvitation');
        return;
      }

      if (invitationData.band_id) {
        await supabase.rpc(
          'claim_band_by_invitation',
          {
            invitation_token_param: pendingInvitation,
            profile_id_param: profileId,
          }
        );
      } else if (invitationData.musician_id === null) {
        await supabase.rpc('accept_invitation_by_token', {
          token: pendingInvitation,
          user_id: user.id
        });
      }

      localStorage.removeItem('pendingInvitation');
    } catch (err) {
      localStorage.removeItem('pendingInvitation');
    }
  };

  const onSubmit = async (data: CreateProfileFormData) => {
    if (isSubmitting) return; // Prevent multiple submissions
    
    if (!user) {
      setError('You must be logged in to create a profile');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');

      // If artist_name is empty and is_musician is true, use full_name as artist_name
      const artistName = data.is_musician && data.artist_name ? data.artist_name : data.full_name;

      // Create new profile
      const { data: newProfile, error: insertError } = await supabase
        .from('profiles')
        .insert({
          user_id: user.id,
          full_name: data.full_name,
          artist_name: artistName,
          gender: data.gender,
          is_musician: data.is_musician,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Process any pending invitation
      if (pendingInvitation) {
        await processInvitation(newProfile.id);
      }

      // Refresh the profile to trigger user menu update
      await refreshProfile();

      // Navigate based on musician status
      navigate(data.is_musician ? '/become-musician' : '/profile');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading while checking profile status
  if (profileLoading) {
    return (
      <div className="container mx-auto px-6 md:py-24">
        <div className="text-center text-light/60">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          Loading...
        </div>
      </div>
    );
  }

  // Don't render the form if user is not logged in or already has a profile
  // (the useEffect will handle redirects)
  if (!user || profile) {
    return null;
  }

  return (
    <div className="container mx-auto px-6 pb-8 md:py-24">
      <Title>{t('auth.createProfile.title')}</Title>

      <div className="max-w-2xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:space-x-4 mb-12">
          <div className="flex-1">
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full bg-secondary text-light flex items-center justify-center font-semibold">
                1
              </div>
              <div className="ml-3">
                <h3 className="font-medium text-light">{t('auth.createProfile.basicInfo')}</h3>
                <p className="text-sm text-light/60">{t('auth.createProfile.basicInfoSubtitle')}</p>
              </div>
            </div>
            <div className="mt-2 h-1 bg-secondary rounded-full" />
          </div>

          <div className="flex-1">
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${isMusician ? 'bg-gray-custom/20 text-light' : 'bg-gray-custom/10 text-light/20'}`}>
                2
              </div>
              <div className="ml-3">
                <h3 className={`font-medium ${isMusician ? 'text-light/40' : 'text-light/20'}`}>{t('auth.createProfile.musicianProfile')}</h3>
                <p className="text-sm text-light/60">{t('auth.createProfile.musicianProfileSubtitle')}</p>
              </div>
            </div>
            <div className={`mt-2 h-1 rounded-full ${isMusician ? 'bg-gray-custom/20' : 'bg-gray-custom/10'}`} />
          </div>
        </div>

        <div className="panel">
          {error && (
            <div className="bg-red-500/10 text-red-500 p-4 rounded-lg mb-6 text-center">
              {error}
            </div>
          )}
          
          {pendingInvitation && (
            <div className="bg-green-500/10 text-green-500 p-4 rounded-lg mb-6 text-center">
              {t('auth.createProfile.pendingInvitation')}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-light/80 mb-2">
                {t('auth.createProfile.fullNameLabel')}
              </label>
              <input
                type="text"
                id="full_name"
                {...register('full_name')}
                className="block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                placeholder={t('auth.createProfile.fullNamePlaceholder')}
              />
              {errors.full_name && (
                <p className="mt-1 text-sm text-red-500">{errors.full_name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-light/80 mb-2">
                {t('auth.createProfile.genderLabel')}
              </label>
              <div className="flex flex-col sm:flex-row gap-2 sm:space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    value="male"
                    {...register('gender')}
                    className="h-4 w-4 border-gray-custom/20 text-primary focus:ring-primary"
                  />
                  <span className="text-light/80">{t('auth.createProfile.maleOption')}</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    value="female"
                    {...register('gender')}
                    className="h-4 w-4 border-gray-custom/20 text-primary focus:ring-primary"
                  />
                  <span className="text-light/80">{t('auth.createProfile.femaleOption')}</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    value="diverse"
                    {...register('gender')}
                    className="h-4 w-4 border-gray-custom/20 text-primary focus:ring-primary"
                  />
                  <span className="text-light/80">{t('auth.createProfile.diverseOption')}</span>
                </label>
              </div>
              {errors.gender && (
                <p className="mt-1 text-sm text-red-500">{errors.gender.message}</p>
              )}
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register('is_musician')}
                  className="h-4 w-4 rounded border-gray-custom/20 text-primary focus:ring-primary"
                  disabled={!!pendingInvitation} // Disable if there's a pending invitation
                />
                <span className="text-light/80">{t('auth.createProfile.isMusicianLabel')}</span>
              </label>
            </div>

            {isMusician && (
              <div>
                <label htmlFor="artist_name" className="block text-sm font-medium text-light/80 mb-2">
                  {t('auth.createProfile.artistNameLabel')} ({t('common.optional')})
                </label>
                <input
                  type="text"
                  id="artist_name"
                  {...register('artist_name')}
                  className="block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                  placeholder={t('auth.createProfile.artistNamePlaceholder')}
                />
                {errors.artist_name && (
                  <p className="mt-1 text-sm text-red-500">{errors.artist_name.message}</p>
                )}
              </div>
            )}

            <button 
              type="submit" 
              className="btn-secondary w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center space-x-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{t('auth.createProfile.creatingProfile')}</span>
                </span>
              ) : (
                t('auth.createProfile.continueButton')
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateProfile;